import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  Modal,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Box,
  Typography,
  Divider,
  Chip,
  IconButton,
  TextField,
  MenuItem,
  TablePagination,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import Sidebar from "./Sidebar";
import { getToken, isValidAdminToken } from "../utils/Authe";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const mockQuizResults = [
  { name: "Quiz 1", score: 85 },
  { name: "Quiz 2", score: 72 },
  { name: "Quiz 3", score: 90 },
  { name: "Quiz 4", score: 65 },
  { name: "Quiz 5", score: 88 },
];

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const Dashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [folders, setFolders] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [quizResults, setQuizResults] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [studentPage, setStudentPage] = useState(0);
  const [studentRowsPerPage, setStudentRowsPerPage] = useState(5);
  const [selectedFolder, setSelectedFolder] = useState("all");
  const [loginDetails, setLoginDetails] = useState(null);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [quizPage, setQuizPage] = useState(0);
  const [quizRowsPerPage, setQuizRowsPerPage] = useState(5);
  const [expandedFolder, setExpandedFolder] = useState(null);
  const [studentPerformanceData, setStudentPerformanceData] = useState([]);
  const [chartViewMode, setChartViewMode] = useState("all");
  const [chartType, setChartType] = useState("avg");
  const [chartSearchTerm, setChartSearchTerm] = useState("");
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editStudent, setEditStudent] = useState(null);
  const [formData, setFormData] = useState({
    student_name: "",
    student_email: "",
    password: "",
    confirmPassword: "",
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    const token = getToken();
    if (!token || !isValidAdminToken(token)) {
      navigate("/login");
    } else {
      Promise.all([fetchStudents(token), fetchFolders(token), fetchStudentPerformance(token)]).finally(() => {
        setLoading(false);
      });
    }
  }, [navigate]);

  const fetchStudents = async (token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/students/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setStudents(data.students);
      } else {
        console.error("Failed to fetch students");
      }
    } catch (error) {
      console.error("Error fetching students:", error);
    }
  };

  const fetchFolders = async (token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/folders/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setFolders(data);
      } else {
        console.error("Failed to fetch courses");
      }
    } catch (error) {
      console.error("Error fetching courses:", error);
    }
  };

  const fetchStudentPerformance = async (token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/quiz/admin/student-average-results/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setStudentPerformanceData(data);
      } else {
        console.error("Failed to fetch performance data");
      }
    } catch (error) {
      console.error("Error fetching performance data:", error);
    }
  };

  const fetchQuizResults = async (studentId) => {
    try {
      const token = getToken();
      const response = await fetch(
        `${API_BASE_URL}/students/${studentId}/quiz-results/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setQuizResults(data);
        setModalOpen(true);
      } else {
        console.error("Failed to fetch quiz results");
      }
    } catch (error) {
      console.error("Error fetching quiz results:", error);
    }
  };

  const handleExportStudentAvgResults = async () => {
    try {
      const token = getToken();
      const response = await fetch(
        `${API_BASE_URL}/export-student-avg-results/`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error(`Export failed: ${response.status} ${response.statusText}`);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Student_Average_Results_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Export failed:", error);
      alert(`Export failed: ${error.message}`);
    }
  };

  const handleDeleteStudent = async () => {
    try {
      const token = getToken();
      const response = await fetch(
        `${API_BASE_URL}/students/${studentToDelete.id}/`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.ok) {
        await fetchStudents(token);
        setDeleteDialogOpen(false);
      } else {
        console.error("Failed to delete student");
      }
    } catch (error) {
      console.error("Error deleting student:", error);
    }
  };

  const openDeleteDialog = (student) => {
    setStudentToDelete(student);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  const handleViewResult = (student) => {
    setSelectedStudent(student);
    setQuizPage(0);
    setExpandedFolder(null);
    fetchQuizResults(student.id);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setQuizResults(null);
  };

  const filteredStudents = students.filter((student) => {
    const matchesSearch =
      student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFolder =
      selectedFolder === "all" ||
      student.assigned_folders.some((folder) => folder.id === selectedFolder);
    return matchesSearch && matchesFolder;
  });

  const paginatedStudents = filteredStudents.slice(
    studentPage * studentRowsPerPage,
    studentPage * studentRowsPerPage + studentRowsPerPage
  );

  const handleStudentPageChange = (event, newPage) => {
    setStudentPage(newPage);
  };

  const handleStudentRowsPerPageChange = (event) => {
    setStudentRowsPerPage(parseInt(event.target.value, 10));
    setStudentPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setStudentPage(0);
  };

  const handleFolderFilterChange = (event) => {
    setSelectedFolder(event.target.value);
    setStudentPage(0);
  };

  const getFilteredChartData = () => {
    if (!studentPerformanceData || studentPerformanceData.length === 0) return [];
    let filtered = [...studentPerformanceData];
    if (chartSearchTerm.trim()) {
      filtered = filtered.filter((student) =>
        student.student_name.toLowerCase().includes(chartSearchTerm.toLowerCase())
      );
    }
    switch (chartViewMode) {
      case "top10":
        filtered.sort((a, b) => b.average_passing_percentage - a.average_passing_percentage);
        return filtered.slice(0, 10);
      case "bottom10":
        filtered.sort((a, b) => a.average_passing_percentage - b.average_passing_percentage);
        return filtered.slice(0, 10);
      case "recent10":
        return filtered.slice(-10).reverse();
      case "all":
        return filtered;
      default:
        return filtered;
    }
  };

  const fetchLoginDetails = async (studentId) => {
    try {
      const token = getToken();
      const response = await fetch(
        `${API_BASE_URL}/quiz/admin/student-login-details/${studentId}/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setLoginDetails(data);
        setLoginModalOpen(true);
      } else {
        console.error("Failed to fetch login details");
      }
    } catch (error) {
      console.error("Error fetching login details:", error);
    }
  };

  const openEditModal = (student) => {
    setEditStudent(student);
    setFormData({
      student_name: student.name,
      student_email: student.email,
      password: "",
      confirmPassword: "",
    });
    setFormErrors({});
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setFormData({
      student_name: "",
      student_email: "",
      password: "",
      confirmPassword: "",
    });
    setFormErrors({});
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.student_name.trim()) errors.student_name = "Name is required";
    if (!formData.student_email.trim()) errors.student_email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.student_email))
      errors.student_email = "Invalid email format";
    if (formData.password && formData.password !== formData.confirmPassword)
      errors.confirmPassword = "Passwords do not match";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleEditSubmit = async () => {
    if (!validateForm()) return;
    try {
      const token = getToken();
      const payload = {
        student_name: formData.student_name,
        student_email: formData.student_email,
        ...(formData.password && { password: formData.password }),
      };
      const response = await fetch(`${API_BASE_URL}/students/${editStudent.id}/update/`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        await fetchStudents(token);
        closeEditModal();
      } else {
        console.error("Failed to update student");
      }
    } catch (error) {
      console.error("Error updating student:", error);
    }
  };

 if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{background : theme.palette.background.default   

        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        // maxWidth: 1200,
        // mx: "auto",
        // minHeight: "100vh",
        background: theme.palette.background.default,
        // borderRadius: "24px",
        // boxShadow: "0 20px 60px rgba(0, 0, 0, 0.1)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <div>
          <Typography
            variant="h3"
            gutterBottom
            sx={{
              fontWeight: "800",
              background: theme.palette.mode === 'light'
                ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            Admin Dashboard
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{ color: theme.palette.text.secondary, fontWeight: "600", mt: 1 }}
          >
            Welcome! You are logged in as a verified admin.
          </Typography>
        </div>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: 2,
          mb: 4,
        }}
      >
        <Paper
          sx={{
            p: 3,
            textAlign: "center",
            bgcolor: theme.palette.mode === 'light' ? "#f0f8ff" : "rgba(25, 118, 210, 0.2)",
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
          }}
        >
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
            Total Students
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: "700", color: "#1976d2" }}>
            {students.length}
          </Typography>
        </Paper>
        <Paper
          sx={{
            p: 3,
            textAlign: "center",
            bgcolor: theme.palette.mode === 'light' ? "#fff0f5" : "rgba(211, 47, 47, 0.2)",
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
          }}
        >
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
            Total Courses
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: "700", color: "#d32f2f" }}>
            {folders.length}
          </Typography>
        </Paper>
      </Box>

      {/* Student Performance Overview */}
      <Paper
        sx={{
          p: 4,
          mb: 4,
          borderRadius: "16px",
          boxShadow: "0 8px 30px rgba(0, 0, 0, 0.1)",
          border: `1px solid ${theme.palette.divider}`,
          transition: "all 0.3s ease",
          "&:hover": {
            boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15)",
          },
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Box sx={{ background: "transparent", borderRadius: "12px", p: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
            <Typography variant="h5" sx={{ color: theme.palette.text.primary, fontWeight: "700" }}>
              Student Performance Overview
            </Typography>
            <Button
              variant="outlined"
              onClick={handleExportStudentAvgResults}
              startIcon={<i className="bi bi-download" style={{ fontSize: "1.2rem" }}></i>}
               sx={{
                                color: "#667eea",
                                borderColor: "#667eea",
                                fontWeight: "600",
                                borderRadius: "12px",
                                textTransform: "none",
                                "&:hover": {
                                    background: theme.palette.mode === 'light' ? "rgba(102, 126, 234, 0.08)" : "rgba(132, 144, 255, 0.08)",
                                    borderColor: "#667eea",
                                },
                            }}
            >
              Export Avg Result
            </Button>
          </Box>
          <Divider sx={{ my: 2, borderColor: theme.palette.divider }} />
          {/* Controls for chart view */}
          <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
            <TextField
              select
              label="View Mode"
              variant="outlined"
              size="small"
              value={chartViewMode}
              onChange={(e) => setChartViewMode(e.target.value)}
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="top10">Top 10 Students</MenuItem>
              <MenuItem value="bottom10">Bottom 10 Students</MenuItem>
              <MenuItem value="recent10">Recent 10 Students</MenuItem>
              <MenuItem value="all">All Students</MenuItem>
            </TextField>
            <TextField
              select
              label="Chart Type"
              variant="outlined"
              size="small"
              value={chartType}
              onChange={(e) => setChartType(e.target.value)}
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="avg">Average Score</MenuItem>
              <MenuItem value="passRate">Pass/Fail Count</MenuItem>
              <MenuItem value="attempts">Total Attempts</MenuItem>
            </TextField>
            <TextField
              label="Search Student"
              variant="outlined"
              size="small"
              value={chartSearchTerm}
              onChange={(e) => setChartSearchTerm(e.target.value)}
              placeholder="Filter by name..."
              sx={{ flexGrow: 1, minWidth: 200 }}
            />
          </Box>
          {/* Summary Cards */}
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 2, mb: 3 }}>
            <Paper sx={{ p: 2, bgcolor: theme.palette.mode === 'light' ? "#e3f2fd" : "rgba(25, 118, 210, 0.2)", borderRadius: "8px", textAlign: "center" }}>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>Avg Performance</Typography>
              <Typography variant="h6" sx={{ fontWeight: "700", color: "#1976d2" }}>
                {getFilteredChartData().length > 0
                  ? (getFilteredChartData().reduce((sum, s) => sum + s.average_passing_percentage, 0) / getFilteredChartData().length).toFixed(1)
                  : 0}%
              </Typography>
            </Paper>
            <Paper sx={{ p: 2, bgcolor: theme.palette.mode === 'light' ? "#e8f5e9" : "rgba(76, 175, 80, 0.2)", borderRadius: "8px", textAlign: "center" }}>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>Total Passes</Typography>
              <Typography variant="h6" sx={{ fontWeight: "700", color: "#4caf50" }}>
                {getFilteredChartData().reduce((sum, s) => sum + s.total_pass, 0)}
              </Typography>
            </Paper>
            <Paper sx={{ p: 2, bgcolor: theme.palette.mode === 'light' ? "#ffebee" : "rgba(244, 67, 54, 0.2)", borderRadius: "8px", textAlign: "center" }}>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>Total Fails</Typography>
              <Typography variant="h6" sx={{ fontWeight: "700", color: "#f44336" }}>
                {getFilteredChartData().reduce((sum, s) => sum + s.total_fail, 0)}
              </Typography>
            </Paper>
            <Paper sx={{ p: 2, bgcolor: theme.palette.mode === 'light' ? "#fff3e0" : "rgba(255, 147, 0, 0.2)", borderRadius: "8px", textAlign: "center" }}>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>Showing</Typography>
              <Typography variant="h6" sx={{ fontWeight: "700", color: "#ff9800" }}>
                {getFilteredChartData().length} / {studentPerformanceData.length}
              </Typography>
            </Paper>
          </Box>
          {/* Scrollable Chart Container */}
          <Box sx={{
            overflowX: "auto",
            overflowY: "hidden",
            "&::-webkit-scrollbar": { height: "8px", mt: 2 },
            "&::-webkit-scrollbar-track": { background: theme.palette.mode === 'light' ? "#f1f5f9" : "rgba(55, 65, 81, 0.5)", borderRadius: "10px" },
            "&::-webkit-scrollbar-thumb": {
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              borderRadius: "10px",
              "&:hover": { background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)" },
            },
          }}>
            <Box sx={{ minWidth: Math.max(600, getFilteredChartData().length * 80) }}>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart
                  data={getFilteredChartData()}
                  margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                >
                  <defs>
                    <linearGradient id="colorPass" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#4caf50" stopOpacity={0.9} />
                      <stop offset="100%" stopColor="#2e7d32" stopOpacity={0.9} />
                    </linearGradient>
                    <linearGradient id="colorFail" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#f44336" stopOpacity={0.9} />
                      <stop offset="100%" stopColor="#c62828" stopOpacity={0.9} />
                    </linearGradient>
                    <linearGradient id="colorAvg" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#667eea" stopOpacity={0.9} />
                      <stop offset="100%" stopColor="#764ba2" stopOpacity={0.9} />
                    </linearGradient>
                    <linearGradient id="colorAttempts" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#ff9800" stopOpacity={0.9} />
                      <stop offset="100%" stopColor="#ef6c00" stopOpacity={0.9} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis
                    dataKey="student_name"
                    angle={-45}
                    textAnchor="end"
                    height={100}
                    tick={{ fill: theme.palette.text.secondary, fontSize: 12, fontWeight: 500 }}
                    stroke={theme.palette.divider}
                  />
                  <YAxis
                    tick={{ fill: theme.palette.text.secondary, fontSize: 12, fontWeight: 500 }}
                    stroke={theme.palette.divider}
                    label={{
                      value: chartType === "avg" ? "Avg Score %" : chartType === "passRate" ? "Count" : "Attempts",
                      angle: -90,
                      position: "insideLeft",
                      style: { fill: theme.palette.text.secondary, fontWeight: 600 }
                    }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: "8px",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    }}
                    formatter={(value, name) => {
                      if (name === "average_passing_percentage") return [value.toFixed(2) + "%", "Avg Score"];
                      if (name === "total_pass") return [value, "Passes"];
                      if (name === "total_fail") return [value, "Fails"];
                      if (name === "total_quizzes_attempted") return [value, "Attempts"];
                      return [value, name];
                    }}
                  />
                  <Legend wrapperStyle={{ paddingTop: "20px" }} iconType="circle" />
                  {chartType === "avg" && (
                    <Bar
                      dataKey="average_passing_percentage"
                      fill="url(#colorAvg)"
                      name="Avg Score %"
                      radius={[8, 8, 0, 0]}
                      maxBarSize={60}
                    />
                  )}
                  {chartType === "passRate" && (
                    <>
                      <Bar
                        dataKey="total_pass"
                        fill="url(#colorPass)"
                        name="Passes"
                        radius={[8, 8, 0, 0]}
                        maxBarSize={60}
                      />
                      <Bar
                        dataKey="total_fail"
                        fill="url(#colorFail)"
                        name="Fails"
                        radius={[8, 8, 0, 0]}
                        maxBarSize={60}
                      />
                    </>
                  )}
                  {chartType === "attempts" && (
                    <Bar
                      dataKey="total_quizzes_attempted"
                      fill="url(#colorAttempts)"
                      name="Total Attempts"
                      radius={[8, 8, 0, 0]}
                      maxBarSize={60}
                    />
                  )}
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Box>
          {/* Legend and Scroll Hint */}
          <Box sx={{
            mt: 3,
            pt: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
            display: "flex",
            gap: 3,
            flexWrap: "wrap"
          }}>
            {chartType === "passRate" && (
              <>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)"
                  }} />
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>Passes</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #f44336 0%, #c62828 100%)"
                  }} />
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>Fails</Typography>
                </Box>
              </>
            )}
            {chartType === "avg" && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                }} />
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>Avg Score %</Typography>
              </Box>
            )}
            {chartType === "attempts" && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #ff9800 0%, #ef6c00 100%)"
                }} />
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>Total Attempts</Typography>
              </Box>
            )}
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, ml: "auto" }}>
              💡 Scroll horizontally to view all results
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Quiz Results Modal */}
      <Modal open={modalOpen} onClose={handleCloseModal}>
        <Box
          sx={{
            p: 0,
            bgcolor: theme.palette.background.paper,
            mx: "auto",
            mt: "3%",
            mb: "3%",
            width: "90%",
            maxWidth: 900,
            maxHeight: "90vh",
            borderRadius: "20px",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.2)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              p: 3,
              background: theme.palette.mode === 'light'
                ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
              color: "white",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                Quiz Results for {selectedStudent?.name} ({selectedStudent?.email})
              </Typography>
              {quizResults && (
                <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
                  Client Code: {quizResults.client_code}
                </Typography>
              )}
            </Box>
          </Box>

          <Box sx={{ p: 3, overflowY: "auto", flex: 1 }}>
            {quizResults ? (
              <Box sx={{ overflowY: "auto", flex: 1 }}>
                {quizResults ? (
                  <Box>
                    {Object.entries(
                      quizResults.quiz_history.reduce((acc, quiz) => {
                        if (!acc[quiz.folder_name]) acc[quiz.folder_name] = [];
                        acc[quiz.folder_name].push(quiz);
                        return acc;
                      }, {})
                    ).map(([folderName, quizzes]) => (
                      <Accordion
                        key={folderName}
                        expanded={expandedFolder === folderName}
                        onChange={() => setExpandedFolder(expandedFolder === folderName ? null : folderName)}
                        sx={{ boxShadow: "none", border: `1px solid ${theme.palette.divider}`, mb: 1, borderRadius: "8px" }}
                      >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography sx={{ fontWeight: "600" }}>{folderName}</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <TableContainer component={Paper} sx={{ boxShadow: "none", borderRadius: "8px" }}>
                            <Table>
                              <TableHead>
                                <TableRow sx={{ bgcolor: theme.palette.mode === 'light' ? "#f8f9ff" : "rgba(55, 65, 81, 0.5)" }}>
                                  <TableCell sx={{ fontWeight: "600", color: theme.palette.text.primary }}>Quiz Name</TableCell>
                                  <TableCell sx={{ fontWeight: "600", color: theme.palette.text.primary }}>Attempt</TableCell>
                                  <TableCell sx={{ fontWeight: "600", color: theme.palette.text.primary }}>Score (%)</TableCell>
                                  <TableCell sx={{ fontWeight: "600", color: theme.palette.text.primary }}>Result</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {quizzes
                                  .flatMap((quiz) =>
                                    quiz.attempts.map((attempt) => ({
                                      ...attempt,
                                      quiz_name: quiz.quiz_name,
                                    }))
                                  )
                                  .slice(quizPage * quizRowsPerPage, quizPage * quizRowsPerPage + quizRowsPerPage)
                                  .map((attempt, index) => (
                                    <TableRow key={index} sx={{ "&:hover": { bgcolor: theme.palette.mode === 'light' ? "#f5f5f5" : "rgba(55, 65, 81, 0.3)" } }}>
                                      <TableCell sx={{ color: theme.palette.text.primary }}>{attempt.quiz_name}</TableCell>
                                      <TableCell sx={{ color: theme.palette.text.primary }}>{attempt.attempt_number}</TableCell>
                                      <TableCell sx={{ color: theme.palette.text.primary }}>{attempt.score_percentage.toFixed(2)}%</TableCell>
                                      <TableCell>
                                        <Chip
                                          label={attempt.result}
                                          size="small"
                                          sx={{
                                            bgcolor: attempt.result === "Pass" ? "#4caf50" : "#f44336",
                                            color: "white",
                                            fontWeight: "bold",
                                            borderRadius: "8px",
                                          }}
                                        />
                                      </TableCell>
                                    </TableRow>
                                  ))}
                              </TableBody>
                            </Table>
                            <TablePagination
                              rowsPerPageOptions={[5, 10, 25]}
                              component="div"
                              count={quizzes.flatMap((quiz) => quiz.attempts).length}
                              rowsPerPage={quizRowsPerPage}
                              page={quizPage}
                              onPageChange={(event, newPage) => setQuizPage(newPage)}
                              onRowsPerPageChange={(event) => {
                                setQuizRowsPerPage(parseInt(event.target.value, 10));
                                setQuizPage(0);
                              }}
                              sx={{ borderTop: `1px solid ${theme.palette.divider}`, bgcolor: theme.palette.mode === 'light' ? "#f5f5f5" : "rgba(55, 65, 81, 0.5)" }}
                            />
                          </TableContainer>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ textAlign: "center", py: 5 }}>
                    <Typography variant="h6" color={theme.palette.text.secondary}>
                      No quiz results found.
                    </Typography>
                  </Box>
                )}
              </Box>
            ) : (
              <Box sx={{ textAlign: "center", py: 5 }}>
                <Typography variant="h6" color={theme.palette.text.secondary}>
                  No quiz results found.
                </Typography>
              </Box>
            )}
          </Box>

          <Box
            sx={{
              p: 2,
              bgcolor: theme.palette.mode === 'light' ? "#f5f5f5" : "rgba(55, 65, 81, 0.5)",
              borderTop: `1px solid ${theme.palette.divider}`,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Button
              variant="contained"
              onClick={handleCloseModal}
              sx={{
                minWidth: 120,
                borderRadius: "12px",
                padding: "10px 24px",
                fontWeight: "600",
                textTransform: "none",
                background: theme.palette.mode === 'light'
                  ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                  : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
                "&:hover": {
                  background: theme.palette.mode === 'light'
                    ? "linear-gradient(135deg, #764ba2 0%, #667eea 100%)"
                    : "linear-gradient(135deg, #9333ea 0%, #818cf8 100%)",
                },
              }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Delete Student Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="xs"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: "12px",
            padding: "10px",
          },
        }}
      >
        <DialogTitle
          id="alert-dialog-title"
          sx={{
            fontWeight: "bold",
            color: "error.main",
            fontSize: "1.3rem",
            display: "flex",
            alignItems: "center",
          }}
        >
          <DeleteIcon sx={{ color: "error.main", mr: 1 }} />
          Delete Student
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: "medium" }}>
              Are you sure you want to delete {studentToDelete?.name}? <br />
              This action cannot be undone.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: "space-between" }}>
          <Button
            onClick={closeDeleteDialog}
            variant="outlined"
            color="inherit"
            sx={{
              borderColor: "grey.400",
              padding: "8px 20px",
              fontSize: "0.9rem",
              textTransform: "none",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteStudent}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            sx={{
              boxShadow: "none",
              padding: "8px 20px",
              fontSize: "0.9rem",
              textTransform: "none",
            }}
          >
            Delete Permanently
          </Button>
        </DialogActions>
      </Dialog>

      {/* Login Modal */}
      <Modal open={loginModalOpen} onClose={() => setLoginModalOpen(false)}>
        <Box
          sx={{
            p: 0,
            bgcolor: theme.palette.background.paper,
            mx: "auto",
            mt: "3%",
            mb: "3%",
            width: "90%",
            maxWidth: 900,
            maxHeight: "90vh",
            borderRadius: "20px",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.2)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              p: 3,
              background: theme.palette.mode === 'light'
                ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
              color: "white",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                Login Details for {loginDetails?.student_name} ({loginDetails?.email})
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
                Client Code: {loginDetails?.client_code}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ p: 3, overflowY: "auto", flex: 1 }}>
            {loginDetails ? (
              <Box>
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2, mb: 3 }}>
                  <Paper sx={{ p: 2, bgcolor: theme.palette.mode === 'light' ? "#e3f2fd" : "rgba(25, 118, 210, 0.2)", borderRadius: "8px", textAlign: "center" }}>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                      Total Logins
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: "700", color: "#1976d2" }}>
                      {loginDetails.total_logins}
                    </Typography>
                  </Paper>
                  <Paper sx={{ p: 2, bgcolor: theme.palette.mode === 'light' ? "#e8f5e9" : "rgba(76, 175, 80, 0.2)", borderRadius: "8px", textAlign: "center" }}>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                      Total Duration (mins)
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: "700", color: "#4caf50" }}>
                      {loginDetails.total_duration_minutes.toFixed(2)}
                    </Typography>
                  </Paper>
                  <Paper sx={{ p: 2, bgcolor: theme.palette.mode === 'light' ? "#ffebee" : "rgba(244, 67, 54, 0.2)", borderRadius: "8px", textAlign: "center" }}>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                      Last Login
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: "700", color: "#f44336" }}>
                      {loginDetails.last_login ? new Date(loginDetails.last_login).toLocaleString() : "N/A"}
                    </Typography>
                  </Paper>
                </Box>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: "600", color: theme.palette.text.primary }}>
                  Login Sessions
                </Typography>
                <TableContainer
                  component={Paper}
                  sx={{
                    marginTop: "16px",
                    borderRadius: "12px",
                    boxShadow: "none",
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: theme.palette.mode === 'light' ? "#f8f9ff" : "rgba(55, 65, 81, 0.5)" }}>
                        <TableCell sx={{ fontWeight: "600", color: theme.palette.text.primary }}>Login Time</TableCell>
                        <TableCell sx={{ fontWeight: "600", color: theme.palette.text.primary }}>Logout Time</TableCell>
                        <TableCell sx={{ fontWeight: "600", color: theme.palette.text.primary }}>Duration (mins)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loginDetails.sessions
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((session, index) => (
                          <TableRow key={index} sx={{ "&:hover": { bgcolor: theme.palette.mode === 'light' ? "#f5f5f5" : "rgba(55, 65, 81, 0.3)" } }}>
                            <TableCell sx={{ color: theme.palette.text.primary }}>{new Date(session.login_time).toLocaleString()}</TableCell>
                            <TableCell sx={{ color: theme.palette.text.primary }}>
                              {session.logout_time ? new Date(session.logout_time).toLocaleString() : "Still logged in"}
                            </TableCell>
                            <TableCell sx={{ color: theme.palette.text.primary }}>{session.duration_minutes.toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={loginDetails.sessions.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(event, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(event) => {
                      setRowsPerPage(parseInt(event.target.value, 10));
                      setPage(0);
                    }}
                    sx={{
                      borderTop: `1px solid ${theme.palette.divider}`,
                      bgcolor: theme.palette.mode === 'light' ? "#f5f5f5" : "rgba(55, 65, 81, 0.5)",
                    }}
                  />
                </TableContainer>
              </Box>
            ) : (
              <Box sx={{ textAlign: "center", py: 5 }}>
                <Typography variant="h6" color={theme.palette.text.secondary}>
                  No login details found.
                </Typography>
              </Box>
            )}
          </Box>

          <Box
            sx={{
              p: 2,
              bgcolor: theme.palette.mode === 'light' ? "#f5f5f5" : "rgba(55, 65, 81, 0.5)",
              borderTop: `1px solid ${theme.palette.divider}`,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Button
              variant="contained"
              onClick={() => setLoginModalOpen(false)}
              sx={{
                minWidth: 120,
                borderRadius: "12px",
                padding: "10px 24px",
                fontWeight: "600",
                textTransform: "none",
                background: theme.palette.mode === 'light'
                  ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                  : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
                "&:hover": {
                  background: theme.palette.mode === 'light'
                    ? "linear-gradient(135deg, #764ba2 0%, #667eea 100%)"
                    : "linear-gradient(135deg, #9333ea 0%, #818cf8 100%)",
                },
              }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Edit Modal */}
      <Modal open={editModalOpen} onClose={closeEditModal}>
        <Box
          sx={{
            p: 0,
            bgcolor: theme.palette.background.paper,
            mx: "auto",
            mt: "3%",
            mb: "3%",
            width: "90%",
            maxWidth: 500,
            maxHeight: "90vh",
            borderRadius: "20px",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.2)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              p: 3,
              background: theme.palette.mode === 'light'
                ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
              color: "white",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              Edit Student
            </Typography>
            <IconButton
              onClick={closeEditModal}
              sx={{
                color: "white",
                "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ p: 3, overflowY: "auto", flex: 1 }}>
            <TextField
              fullWidth
              margin="normal"
              label="Name"
              name="student_name"
              value={formData.student_name}
              onChange={handleFormChange}
              error={!!formErrors.student_name}
              helperText={formErrors.student_name}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Email"
              name="student_email"
              value={formData.student_email}
              onChange={handleFormChange}
              error={!!formErrors.student_email}
              helperText={formErrors.student_email}
            />
            <TextField
              fullWidth
              margin="normal"
              label="New Password (leave blank to keep current)"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleFormChange}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Confirm Password"
              name="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={handleFormChange}
              error={!!formErrors.confirmPassword}
              helperText={formErrors.confirmPassword}
            />
          </Box>

          <Box
            sx={{
              p: 2,
              bgcolor: theme.palette.mode === 'light' ? "#f5f5f5" : "rgba(55, 65, 81, 0.5)",
              borderTop: `1px solid ${theme.palette.divider}`,
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
            }}
          >
            <Button
              variant="outlined"
              onClick={closeEditModal}
              sx={{
                minWidth: 120,
                borderRadius: "12px",
                padding: "10px 24px",
                fontWeight: "600",
                textTransform: "none",
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleEditSubmit}
              sx={{
                minWidth: 120,
                borderRadius: "12px",
                padding: "10px 24px",
                fontWeight: "600",
                textTransform: "none",
                background: theme.palette.mode === 'light'
                  ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                  : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
                "&:hover": {
                  background: theme.palette.mode === 'light'
                    ? "linear-gradient(135deg, #764ba2 0%, #667eea 100%)"
                    : "linear-gradient(135deg, #9333ea 0%, #818cf8 100%)",
                },
              }}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default Dashboard;
